import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { asApiError } from '@/services/apiError'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { ProjectionValue } from '@/components/px/ProjectionValue'
import { SectionLabel } from '@/components/px/SectionLabel'
import { adminNav } from '@/components/px/nav'
import {
  closeRateCard,
  createRateCard,
  fetchRateCards,
  rateCardsErrorDismissed,
} from '@/features/rates/slices/rateCardsSlice'
import {
  selectRateCards,
  selectRateCardsError,
  selectRateCardsLoading,
  selectRateCardsMutating,
} from '@/features/rates/selectors'
import type { CreateRateCardInput, RateCard } from '@/features/rates/types'
import { formatDay, formatMoney } from '@/lib/money'
import { useAppDispatch, useAppSelector } from '@/store'
import { selectActiveRoleKey } from '@/features/auth/slices/sessionSlice'

/**
 * RATE CARDS (`/admin/rates`, PX-W6.4 AC-3): the org's priced rows — list,
 * create, close. The destination is admin-only via the declarative
 * `ROLE_ACCESS` table (a non-admin opening the URL sees `RoleGuarded`'s
 * blocked page — never this surface, never a disabled-and-visible form).
 *
 * The card is APPEND-ONLY (no edit endpoint exists or is offered): a
 * correction is a new row with a later `validFrom`, an ending is the close
 * with its required EXCLUSIVE `validTo`. `amountMinor` is entered as a major
 * amount and converted ONCE at this boundary — money is an integer minor
 * count everywhere past the form. Seniority/role are optional and their
 * omission is MEANINGFUL (the office-default row).
 */

/** The offices `MongoProject.office` spells — the resolver compares verbatim. */
const OFFICES: readonly string[] = ['NL', 'BE', 'FR', 'Kosovo']

/** Currency options for the select — `^[A-Z]{3}$` on the wire either way. */
const CURRENCIES: readonly string[] = ['EUR', 'USD', 'GBP', 'CHF']

/** The create form's draft state; money is a major-unit string until submit. */
interface CardFormDraft {
  office: string
  seniority: string
  role: string
  amount: string
  currency: string
  validFrom: string
  validTo: string
}

/**
 * The user's LOCAL calendar day as `"YYYY-MM-DD"` (not UTC — a rate starting
 * "today" must mean the user's today; the wire day is a human calendar day).
 */
const localTodayIso = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`
}

const emptyDraft = (): CardFormDraft => ({
  office: OFFICES[0]!,
  seniority: '',
  role: '',
  amount: '',
  currency: CURRENCIES[0]!,
  validFrom: localTodayIso(),
  validTo: ''
})

export default function RateCardsPage() {
  const { t } = useTranslation('commercial')
  const dispatch = useAppDispatch()
  const role = useAppSelector(selectActiveRoleKey)
  const cards = useAppSelector(selectRateCards)
  const loading = useAppSelector(selectRateCardsLoading)
  const error = useAppSelector(selectRateCardsError)
  const mutating = useAppSelector(selectRateCardsMutating)

  const [draft, setDraft] = useState<CardFormDraft>(emptyDraft)
  const [formError, setFormError] = useState<string | null>(null)
  const [closingId, setClosingId] = useState<string | null>(null)
  const [closingDate, setClosingDate] = useState('')

  useEffect(() => {
    void dispatch(fetchRateCards())
  }, [dispatch])

  const onRetryClicked = (): void => {
    // Dismiss FIRST — it re-arms the cache (the fetchCache Retry contract),
    // so a failed post-mutation refetch is refetchable, not skipped.
    dispatch(rateCardsErrorDismissed())
    void dispatch(fetchRateCards())
  }

  const onCreateClicked = async (): Promise<void> => {
    const amount = Number(draft.amount)
    if (
      draft.office === '' ||
      draft.validFrom === '' ||
      draft.amount === '' ||
      !Number.isFinite(amount) ||
      amount < 0
    ) {
      setFormError(t('commercial:rates.form.invalid'))
      return
    }
    // Minor units, computed ONCE here: an integer count of the smallest unit.
    const amountMinor = Math.round(amount * 100)
    const input: CreateRateCardInput = {
      office: draft.office,
      seniority: draft.seniority.trim() === '' ? undefined : draft.seniority.trim(),
      role: draft.role.trim() === '' ? undefined : draft.role.trim(),
      amountMinor,
      currency: draft.currency,
      validFrom: draft.validFrom,
      validTo: draft.validTo.trim() === '' ? undefined : draft.validTo.trim()
    }
    setFormError(null)
    const result = await dispatch(createRateCard(input))
    // The draft SURVIVES a failed create (review minor e) — a refused POST
    // (e.g. an overlapping window) must not eat the admin's typed row.
    if (createRateCard.fulfilled.match(result)) setDraft(emptyDraft())
  }

  const onCloseStarted = (card: RateCard): void => {
    setClosingId(card.id)
    setClosingDate('')
  }

  const onCloseConfirmed = (): void => {
    if (closingId === null) return
    if (closingDate === '') {
      setFormError(t('commercial:rates.close.required'))
      return
    }
    setFormError(null)
    void dispatch(closeRateCard({ cardId: closingId, input: { validTo: closingDate } }))
    setClosingId(null)
    setClosingDate('')
  }

  const inputClass =
    'rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring'

  return (
    <AppShell
      moduleLabel={t('commercial:chrome.module')}
      breadcrumb={<span>{t('commercial:rates.title')}</span>}
      contextPill={t('commercial:chrome.administration').toUpperCase()}
      nav={adminNav(role)}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('commercial:rates.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('commercial:rates.lede')}</p>
      </header>

      <section className="mt-5 rounded-lg border border-border bg-card px-4 py-3.5" aria-label={t('commercial:rates.form.title')}>
        <SectionLabel>{t('commercial:rates.form.title')}</SectionLabel>
        <div className="mt-2 grid gap-3 md:grid-cols-4">
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.office')}
            </span>
            <select
              value={draft.office}
              onChange={(e) => setDraft({ ...draft, office: e.target.value })}
              className={`${inputClass} w-full`}
            >
              {OFFICES.map((office) => (
                <option key={office} value={office}>
                  {office}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.seniority')}
            </span>
            <input
              type="text"
              maxLength={64}
              value={draft.seniority}
              onChange={(e) => setDraft({ ...draft, seniority: e.target.value })}
              className={`${inputClass} w-full`}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.role')}
            </span>
            <input
              type="text"
              maxLength={64}
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              className={`${inputClass} w-full`}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.amount')}
            </span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
              className={`${inputClass} w-full`}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.currency')}
            </span>
            <select
              value={draft.currency}
              onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              className={`${inputClass} w-full`}
            >
              {CURRENCIES.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.validFrom')}
            </span>
            <input
              type="date"
              value={draft.validFrom}
              onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })}
              className={`${inputClass} w-full`}
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-xs uppercase tracking-[0.06em] text-muted-foreground">
              {t('commercial:rates.form.validTo')}
            </span>
            <input
              type="date"
              value={draft.validTo}
              onChange={(e) => setDraft({ ...draft, validTo: e.target.value })}
              className={`${inputClass} w-full`}
            />
          </label>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void onCreateClicked()}
            disabled={mutating}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {t('commercial:rates.form.action')}
          </button>
          <p className="text-xs text-muted-foreground">{t('commercial:rates.form.validToNote')}</p>
        </div>
        {formError !== null && (
          <p role="alert" className="mt-2 text-sm text-blocked">
            {formError}
          </p>
        )}
      </section>

      <section className="mt-4" aria-label={t('commercial:rates.table.title')}>
        {loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          // AC-6.2: the server refused the org's cards - a fact, not a failure.
          <ForbiddenPanel requested="/{orgId}/rate-cards" />
        ) : error !== null ? (
          <ErrorPanel error={asApiError(error)} onRetryClicked={onRetryClicked} />
        ) : cards.length === 0 ? (
          <EmptyPanel
            title={t('commercial:rates.empty.title')}
            body={t('commercial:rates.empty.body')}
          />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="v-row border-b border-border">
                  <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('commercial:rates.table.office')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('commercial:rates.table.seniority')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('commercial:rates.table.role')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('commercial:rates.table.amount')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('commercial:rates.table.window')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {t('commercial:rates.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {cards.map((card) => (
                  <tr key={card.id} className="v-row border-b border-border last:border-b-0">
                    <td className="px-3 py-2.5 text-foreground">{card.office}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {card.seniority ?? t('commercial:rates.officeDefault')}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">{card.role ?? '—'}</td>
                    <td className="v-figure px-3 py-2.5 text-right tabular-nums text-foreground">
                      <ProjectionValue
                        value={card.amountMinor}
                        format={(n) => formatMoney(n, card.currency)}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {card.validTo === undefined
                        ? `${formatDay(card.validFrom)} · ${t('commercial:common.openEnded')}`
                        : `${formatDay(card.validFrom)} – ${formatDay(card.validTo)}`}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-right">
                      {/* Append-only: the ONLY write on an existing card is the
                          close, with its required exclusive validTo. */}
                      {card.validTo === undefined ? (
                        closingId === card.id ? (
                          <span className="inline-flex items-center gap-2">
                            <input
                              type="date"
                              aria-label={t('commercial:rates.close.dateLabel')}
                              value={closingDate}
                              onChange={(e) => setClosingDate(e.target.value)}
                              className={`${inputClass} w-36`}
                            />
                            <button
                              type="button"
                              onClick={onCloseConfirmed}
                              disabled={mutating}
                              className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                              {t('commercial:rates.close.confirm')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setClosingId(null)}
                              className="rounded-md border border-input px-2 py-1 text-xs text-foreground hover:bg-muted"
                            >
                              {t('commercial:common.cancel')}
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => onCloseStarted(card)}
                            className="rounded-md border border-input px-2 py-1 text-xs text-foreground hover:bg-muted"
                          >
                            {t('commercial:rates.close.action')}
                          </button>
                        )
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </AppShell>
  )
}
